import { useEffect, useRef } from 'react';
import { useFlowSocket } from '@/hooks/use-flow-socket';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { getObjectId } from '@/helpers/object.id.helper';
import { isEntityOfMode } from '@/components/events/helpers/mode.helper';
import { debugFlow } from '../flow-detail.utils';
import { normalizeFlowForPlanView } from '../normalize-flow-plan-view';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { useModeStore } from '@/state/stores/use-mode-store';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { EventSlotDto } from '@/types/dtos/events/response/event.dto';
import type { QueryClient } from '@tanstack/react-query';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { slotTimingPreviewStore } from './slot-timing-preview-store';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { patchMediaPlaylistInQueryCache } from '@/state/mutations/media/patch-media-playlist-cache';
import { patchMediaAssetApprovalInQueryCache } from '@/state/mutations/media/patch-media-asset-approval-cache';
import { patchFlowMediaSettingsEverywhere } from '@/state/mutations/media/patch-media-settings-cache';

type Params = {
  flowId: string;
  mode: string;
  eventSlotIdParam: string | null;
  event?: { id?: string; slots?: EventSlotDto[] };
  eventPlan?: { id?: string; slots?: EventSlotDto[] };
  eventOrganize?: { id?: string; slots?: EventSlotDto[] };
  queryClient: QueryClient;
  setFlow: (flow?: FlowDto) => void;
  setFlowPlan: (flow?: FlowDto) => void;
  setFlowOrganize: (flow?: FlowDto) => void;
  setIsSyncing: (value: boolean) => void;
  setIsHydrated: (value: boolean) => void;
};

export function useSlotListSync({
  flowId,
  mode,
  eventSlotIdParam,
  event,
  eventPlan,
  eventOrganize,
  queryClient,
  setFlow,
  setFlowPlan,
  setFlowOrganize,
  setIsSyncing,
  setIsHydrated,
}: Params) {
  const lastJoinRef = useRef<{ key?: string; socketId?: string }>({});
  const resolveAvailableMode = (requested?: string) => {
    const storeState = useFlowStore.getState();
    if (requested === 'PLAN' && storeState.flowPlan) return 'PLAN';
    if ((requested === 'ORGANIZE' || requested === 'PERFORM') && storeState.flowOrganize) return requested;
    if (storeState.flowPlan) return 'PLAN';
    if (storeState.flowOrganize) return 'ORGANIZE';
    return mode;
  };

  const resolveFlowGetMode = () => {
    const flowMode = useModeStore.getState().flowMode;
    const storeState = useFlowStore.getState();
    const resolved =
      (flowId && flowMode[flowId]) || (storeState.flowPlan ? 'PLAN' : storeState.flowOrganize ? 'ORGANIZE' : mode);
    const available = resolveAvailableMode(resolved);
    return available === 'PERFORM' ? 'ORGANIZE' : available;
  };

  const hasSlotChanges = (currentSlots?: FlowDto['slots'], nextSlots?: FlowDto['slots']) =>
    JSON.stringify(currentSlots ?? []) !== JSON.stringify(nextSlots ?? []);

  const resolveEventSlotForUpdate = ({
    updatedFlow,
    currentFlow,
  }: {
    updatedFlow: FlowDto;
    currentFlow?: FlowDto;
  }): { slot?: EventSlotDto; shouldSkip: boolean } => {
    const currentSlot = currentFlow?.slot;
    const currentEventSlotId = currentSlot?.id || eventSlotIdParam;
    const eventToUse =
      updatedFlow.mode === 'PLAN'
        ? eventPlan
        : updatedFlow.mode === 'ORGANIZE'
          ? eventOrganize
          : updatedFlow.mode === 'PERFORM'
            ? eventOrganize
            : undefined;

    if (eventSlotIdParam) {
      const slotFromEvent = eventToUse?.slots?.find(
        (slot: EventSlotDto) =>
          slot.contentType === EVENT_SLOT_TYPE.FLOW &&
          (slot.id === eventSlotIdParam || slot.origin === eventSlotIdParam),
      );
      if (slotFromEvent) return { slot: slotFromEvent, shouldSkip: false };
      if (currentSlot && currentFlow?.mode === updatedFlow.mode && currentSlot.id === eventSlotIdParam) {
        return { slot: currentSlot, shouldSkip: false };
      }
      return { slot: undefined, shouldSkip: true };
    }

    if (eventToUse?.slots && currentEventSlotId) {
      const slotFromEvent = eventToUse.slots.find(
        (slot: EventSlotDto) =>
          slot.id === currentEventSlotId &&
          (slot.content === updatedFlow.id ||
            slot.content === updatedFlow.origin ||
            (typeof updatedFlow.origin === 'object' &&
              updatedFlow.origin?.id &&
              slot.content === updatedFlow.origin.id)) &&
          slot.contentType === EVENT_SLOT_TYPE.FLOW,
      );
      if (slotFromEvent) return { slot: slotFromEvent, shouldSkip: false };
    }

    if (currentSlot && currentFlow?.mode === updatedFlow.mode) {
      return { slot: currentSlot, shouldSkip: false };
    }

    return { slot: undefined, shouldSkip: false };
  };

  const resolveActiveViewMode = () => {
    const flowMode = useModeStore.getState().flowMode;
    return resolveAvailableMode((flowId && flowMode[flowId]) || mode);
  };

  const updateEventSessionsFromFlow = (flowToSet: FlowDto) => {
    const slotSessions = (flowToSet.slots ?? [])
      .map((slot) =>
        slot.contentType === 'Session' && slot.content && typeof slot.content === 'object'
          ? (slot.content as SessionDto)
          : undefined,
      )
      .filter(Boolean) as SessionDto[];

    if (!slotSessions.length) return;

    const planUpdates = slotSessions.filter((session) => (session.mode ?? flowToSet.mode) === 'PLAN');
    const organizeUpdates = slotSessions.filter((session) => (session.mode ?? flowToSet.mode) === 'ORGANIZE');

    const mergeSessions = (current: SessionDto[] | undefined, updates: SessionDto[]) => {
      if (!current || !updates.length) return current;
      const updatesById = new Map<string, SessionDto>();
      updates.forEach((session) => {
        updatesById.set(session.id, session);
        const originId = session.origin ? getObjectId(session.origin) : undefined;
        if (originId) {
          updatesById.set(originId, session);
        }
      });

      let changed = false;
      const next = current.map((session) => {
        const originId = session.origin ? getObjectId(session.origin) : undefined;
        const update = updatesById.get(session.id) ?? (originId ? updatesById.get(originId) : undefined);
        if (!update) return session;
        const publishedChanged =
          update.version !== session.version ||
          JSON.stringify(update.publishedModes) !== JSON.stringify(session.publishedModes);
        if (!publishedChanged) return session;
        changed = true;
        return { ...session, ...update };
      });

      return changed ? next : current;
    };

    const eventState = useEventStore.getState();
    const nextPlan = mergeSessions(eventState.eventSessionsPlan, planUpdates);
    const nextOrganize = mergeSessions(eventState.eventSessionsOrganize, organizeUpdates);

    if (nextPlan && nextPlan !== eventState.eventSessionsPlan) {
      eventState.setEventSessionsPlan(nextPlan);
    }
    if (nextOrganize && nextOrganize !== eventState.eventSessionsOrganize) {
      eventState.setEventSessionsOrganize(nextOrganize);
    }
  };

  const { socket, isConnected } = useFlowSocket(flowId, {
    onFlowUpdated: (updatedFlow) => {
      setIsSyncing(true);
      const activeViewMode = resolveActiveViewMode();
      const currentFlow = useFlowStore.getState().flow;
      const normalizedUpdate = normalizeFlowForPlanView(updatedFlow, flowId);
      let flowToSet = normalizedUpdate;
      const { slot: resolvedSlot, shouldSkip } = resolveEventSlotForUpdate({
        updatedFlow: normalizedUpdate,
        currentFlow,
      });
      if (shouldSkip) {
        debugFlow('[FlowDetail] Socket update skipped due to slot mismatch', {
          eventSlotIdParam,
          flowId: updatedFlow.id,
        });
        return;
      }
      slotTimingPreviewStore.clear();
      if (resolvedSlot) {
        const mergedSlot = updatedFlow.slot ? { ...resolvedSlot, ...updatedFlow.slot } : resolvedSlot;
        flowToSet = { ...updatedFlow, slot: mergedSlot };
      }

      const storeState = useFlowStore.getState();
      const modeStoreFlow = normalizedUpdate.mode === 'PLAN' ? storeState.flowPlan : storeState.flowOrganize;
      const dataHasChanged =
        !modeStoreFlow ||
        modeStoreFlow.slot?.id !== flowToSet.slot?.id ||
        modeStoreFlow.slot?.version !== flowToSet.slot?.version ||
        JSON.stringify(modeStoreFlow.publishedModes) !== JSON.stringify(flowToSet.publishedModes) ||
        JSON.stringify(modeStoreFlow.slot?.publishedModes) !== JSON.stringify(flowToSet.slot?.publishedModes) ||
        modeStoreFlow.durationPlannedMinutes !== flowToSet.durationPlannedMinutes ||
        modeStoreFlow.slot?.startOffsetMinutes !== flowToSet.slot?.startOffsetMinutes ||
        hasSlotChanges(modeStoreFlow.slots, flowToSet.slots) ||
        JSON.stringify(modeStoreFlow.mediaPlaylists ?? null) !== JSON.stringify(flowToSet.mediaPlaylists ?? null) ||
        JSON.stringify(modeStoreFlow.media ?? null) !== JSON.stringify(flowToSet.media ?? null) ||
        JSON.stringify(modeStoreFlow.mediaSettings ?? null) !== JSON.stringify(flowToSet.mediaSettings ?? null) ||
        JSON.stringify(modeStoreFlow.effectiveMediaSettings ?? null) !==
          JSON.stringify(flowToSet.effectiveMediaSettings ?? null) ||
        JSON.stringify(modeStoreFlow.mediaSettingsCapabilities ?? null) !==
          JSON.stringify(flowToSet.mediaSettingsCapabilities ?? null);

      if (dataHasChanged) {
        if (normalizedUpdate.mode === 'PLAN') {
          setFlowPlan(flowToSet);
        } else if (normalizedUpdate.mode === 'ORGANIZE') {
          setFlowOrganize(flowToSet);
        }
        if (isEntityOfMode(activeViewMode as never, normalizedUpdate.mode)) {
          setFlow(flowToSet);
        }

        const originId = updatedFlow.origin ? getObjectId(updatedFlow.origin) : flowId;
        const cachedFlow = queryClient.getQueryData<FlowDto>([
          GET_FLOW_QUERY_KEY,
          originId,
          eventSlotIdParam || undefined,
        ]);
        const cacheNeedsUpdate =
          !cachedFlow ||
          cachedFlow.id !== flowToSet.id ||
          cachedFlow.version !== flowToSet.version ||
          cachedFlow.slot?.id !== flowToSet.slot?.id ||
          cachedFlow.slot?.version !== flowToSet.slot?.version ||
          JSON.stringify(cachedFlow.publishedModes) !== JSON.stringify(flowToSet.publishedModes) ||
          JSON.stringify(cachedFlow.slot?.publishedModes) !== JSON.stringify(flowToSet.slot?.publishedModes) ||
          hasSlotChanges(cachedFlow.slots, flowToSet.slots);

        if (cacheNeedsUpdate) {
          queryClient.setQueryData([GET_FLOW_QUERY_KEY, originId, eventSlotIdParam || undefined], flowToSet);
        }
      }

      updateEventSessionsFromFlow(flowToSet);

      if (currentFlow?.slot?.id && event?.id) {
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY, event.id] });
      }

      setIsHydrated(true);
    },
    onFlowInvalidated: (invalidatedFlowId) => {
      if (!(invalidatedFlowId === flowId && socket && isConnected)) return;
      const activeViewMode = resolveActiveViewMode();
      const requestMode = activeViewMode === 'PERFORM' ? 'ORGANIZE' : activeViewMode || resolveFlowGetMode();
      socket.emit(
        'flowGet',
        {
          flowId,
          mode: requestMode,
          eventSlotId: eventSlotIdParam || undefined,
        },
        (flow: FlowDto) => {
          setIsSyncing(true);
          const activeViewMode = resolveActiveViewMode();
          let flowToSet = flow;
          const { slot: resolvedSlot, shouldSkip } = resolveEventSlotForUpdate({
            updatedFlow: flow,
            currentFlow: useFlowStore.getState().flow,
          });
          if (shouldSkip) {
            debugFlow('[FlowDetail] Socket invalidated skipped due to slot mismatch', {
              eventSlotIdParam,
              flowId: flow.id,
            });
            return;
          }
          slotTimingPreviewStore.clear();
          if (resolvedSlot) {
            const mergedSlot = flow.slot ? { ...resolvedSlot, ...flow.slot } : resolvedSlot;
            flowToSet = { ...flow, slot: mergedSlot };
          }
          const mediaHasChanged = (currentMedia?: FlowDto['media'], nextMedia?: FlowDto['media']) => {
            if (!currentMedia && !nextMedia) return false;
            if (!currentMedia || !nextMedia) return true;
            if (currentMedia.length !== nextMedia.length) return true;
            const currentById = new Map(currentMedia.map((media) => [media.id, media]));
            return nextMedia.some((nextMediaItem) => {
              const currentMediaItem = currentById.get(nextMediaItem.id);
              if (!currentMediaItem) return true;
              // Check if media item properties changed
              return (
                currentMediaItem.id !== nextMediaItem.id ||
                currentMediaItem.title !== nextMediaItem.title ||
                currentMediaItem.isRequested !== nextMediaItem.isRequested ||
                JSON.stringify(currentMediaItem.assets) !== JSON.stringify(nextMediaItem.assets)
              );
            });
          };

          const currentStoreFlow = useFlowStore.getState().flow;
          const hasChanged =
            !currentStoreFlow ||
            currentStoreFlow.id !== flowToSet.id ||
            currentStoreFlow.version !== flowToSet.version ||
            currentStoreFlow.slot?.id !== flowToSet.slot?.id ||
            currentStoreFlow.slot?.version !== flowToSet.slot?.version ||
            JSON.stringify(currentStoreFlow.publishedModes) !== JSON.stringify(flowToSet.publishedModes) ||
            JSON.stringify(currentStoreFlow.slot?.publishedModes) !== JSON.stringify(flowToSet.slot?.publishedModes) ||
            hasSlotChanges(currentStoreFlow.slots, flowToSet.slots) ||
            mediaHasChanged(currentStoreFlow.media, flowToSet.media) ||
            JSON.stringify(currentStoreFlow.mediaPlaylists ?? null) !==
              JSON.stringify(flowToSet.mediaPlaylists ?? null);
          if (hasChanged) {
            if (flowToSet.mode === 'PLAN') {
              setFlowPlan(flowToSet);
            } else if (flowToSet.mode === 'ORGANIZE') {
              setFlowOrganize(flowToSet);
            }
            if (isEntityOfMode(activeViewMode as never, flowToSet.mode)) {
              setFlow(flowToSet);
            }
            updateEventSessionsFromFlow(flowToSet);
          }
          // Invalidate React Query cache for flow to ensure consistency
          queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId] });
          void invalidateSimulationCueSheet(queryClient);

          if (event?.id || eventPlan?.id || eventOrganize?.id) {
            queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] });
          }
        },
      );
    },
    onMediaPlaylistUpdated: (notification) => {
      // Instant Assets sync — do not wait for GET_FLOW (starved by cue rebuild ~10–15s).
      if (notification.playlist) {
        patchMediaPlaylistInQueryCache(queryClient, notification.playlist);
      }
      void invalidateSimulationCueSheet(queryClient);
    },
    onFlowMediaSettingsUpdated: (notification) => {
      patchFlowMediaSettingsEverywhere(
        queryClient,
        notification.originId,
        notification.mediaSettings,
        notification.eventOriginId,
      );
    },
    onMediaAssetApprovalUpdated: (notification) => {
      patchMediaAssetApprovalInQueryCache(queryClient, {
        mediaId: notification.mediaId,
        assetVersion: notification.assetVersion,
        isApproved: notification.isApproved,
        sessionOriginId: notification.sessionOriginId,
      });
    },
    onFlowChanged: () => {
      const flowState = useFlowStore.getState().flow;
      const originKey = flowState?.origin ? getObjectId(flowState.origin) : flowId;
      // Background reconcile only — Assets playlist order/show-mode comes from mediaPlaylistUpdated.
      void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, originKey, eventSlotIdParam || undefined] });
      void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, originKey] });
      void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId] });
      void queryClient.refetchQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
      void invalidateSimulationCueSheet(queryClient);
      void queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY] });
      if (event?.id || eventPlan?.id || eventOrganize?.id) {
        void queryClient.refetchQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
        void queryClient.refetchQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] });
        void queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY] });
      }
    },
  });

  useEffect(() => {
    if (socket && flowId && isConnected) {
      const joinKey = `${flowId}:${eventSlotIdParam ?? ''}`;
      const socketId = socket.id;
      if (socketId && lastJoinRef.current.key === joinKey && lastJoinRef.current.socketId === socketId) {
        return;
      }
      lastJoinRef.current = { key: joinKey, socketId };
      socket.emit('flowJoinRoom', { flowId, eventSlotId: eventSlotIdParam || undefined });
    }
  }, [socket, flowId, isConnected, eventSlotIdParam]);

  useEffect(() => {
    if (useFlowStore.getState().flow?.slots && !useFlowStore.getState().flow?.slots?.length && !isConnected) {
      setIsHydrated(true);
    }
  }, [isConnected]);

  return { socket, isConnected, resolveEventSlotForUpdate };
}

export default useSlotListSync;
